import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source';
import { RootState } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';

import packageJson from '../../../../package.json';
import { IAppDispatch, IRootState } from '../../../redux/core-store';
import { setToken } from '../../../redux/slices/auth/auth-slice';
import { logout } from '../../../redux/thunks/user-thunk';
import { fetchStreamContent } from '../../../services/background-fetch/fetch-stream/fetch-stream-content';
import { normalizeRequestUrl } from '../../../services/background-fetch/helpers';
import { ssoService } from '../../../services/sso/sso-service';
import { isContentMode } from '../../../utils/extension-mode';
import { getLogger } from '../../../utils/logger';
import { ERROR_CODES_FOR_RE_AUTHORIZE } from '../../consts';
import { IFailureEvent, StreamEventType, StreamFailureReason, StreamStatus } from '../../types';
import { getLogoutReason } from '../helpers';

const _fetch = isContentMode() ? fetchStreamContent : window.fetch.bind(window);
const logger = getLogger('app-stream');

interface StreamParams<TBody, TEvent> {
    url: URL;
    body: TBody;
    dispatch: IAppDispatch;
    getState: () => RootState<any, any, 'api/base'>;
    abortController?: AbortController;
    acceptedEvents?: StreamEventType[];
    onEvent?: (message: TEvent | IFailureEvent | null, event?: StreamEventType) => void;
    onClose?: () => void;
}

const getStreamHeaders = () => ({
    Accept: 'text/event-stream; charset=utf-8',
    Connection: 'keep-alive',
    'Cache-Control': 'no-cache',
    'Content-Type': 'application/json',
    'x-client-version': packageJson.version,
});

const reAuthMutex = new Mutex();

type WithReAuthFetchParams = {
    dispatch: IAppDispatch;
    input: RequestInfo | URL;
    init?: RequestInit;
    getState: () => RootState<any, any, 'api/base'>;
};

export const _fetchWithReAuth = async (params: WithReAuthFetchParams): Promise<any> => {
    const { dispatch, input, init, getState } = params;

    // get the token from the store explicitly before making the request
    const {
        auth: { token },
    } = getState() as IRootState;

    const withToken = {
        ...init,
        headers: {
            ...init?.headers,
            Authorization: `Bearer ${token}`,
        },
    };

    const result = await _fetch(input, withToken);

    if (!ERROR_CODES_FOR_RE_AUTHORIZE.has(result.status.toString())) {
        return result;
    }

    // it seems we have a stale auth token
    // will try to refresh the token and retry the fetch again

    // check if we are trying to get a fresh token in parallel
    if (reAuthMutex.isLocked()) {
        await reAuthMutex.waitForUnlock();
    } else {
        // get the lock
        const releaseReAuthMutex = await reAuthMutex.acquire();

        try {
            const loginResponse = await ssoService.safeForceRefreshToken();
            if (!loginResponse) {
                // we didn't get a new token, log out user
                const url = normalizeRequestUrl(input);

                dispatch(
                    logout({
                        logoutFromPortal: false,
                        reason: getLogoutReason(String(result.status)),
                        requestUrl: url.toString(),
                    })
                );

                return result;
            }

            dispatch(setToken(loginResponse.accessToken));
        } finally {
            // release the lock
            releaseReAuthMutex();
        }
    }

    return await _fetchWithReAuth(params);
};

export const stream = <TBody, TEvent>(params: StreamParams<TBody, TEvent>) => {
    const { url, body, abortController, acceptedEvents, onEvent, onClose, dispatch, getState } = params;
    let hasFailed = false;

    return fetchEventSource(url.toString(), {
        headers: getStreamHeaders(),
        body: JSON.stringify(body),
        method: 'POST',
        signal: abortController?.signal,
        openWhenHidden: true, // this prevents to pause the stream when the tab is not visible/active
        fetch: (input, init) => _fetchWithReAuth({ dispatch, input, init, getState }),
        async onopen(response) {
            if (response?.ok && response?.headers?.get('content-type')?.includes(EventStreamContentType)) {
                return;
            }

            logger.warn('Invalid response', response, 'will NOT create a stream.');
            hasFailed = true;

            let reason = StreamFailureReason.CONNECTION_FAILED;
            const error = response.statusText;
            const message = await response.json();

            if (ERROR_CODES_FOR_RE_AUTHORIZE.has(response.status.toString())) {
                reason = StreamFailureReason.UNAUTHORIZED;
            } else if (response.status >= 400 && response.status < 500) {
                reason = StreamFailureReason.BAD_REQUEST;
            }

            const errorMessage: IFailureEvent = {
                status: StreamStatus.FAILED,
                error,
                reason,
                debug_info: { message },
            };

            return onEvent?.(errorMessage);
        },
        onerror: (error) => {
            logger.warn('Got an Error while creating stream', error);
            hasFailed = true;
            throw error;
        },
        onmessage: ({ event, data }) => {
            if (!data || (acceptedEvents && !acceptedEvents.includes(event as StreamEventType))) {
                return;
            }

            let message: TEvent | IFailureEvent;

            try {
                message = JSON.parse(data);
            } catch (error) {
                logger.error('Error while parsing event data', error);
                hasFailed = true;
                message = {
                    error: JSON.stringify(error),
                    status: StreamStatus.FAILED,
                    reason: StreamFailureReason.JSON_PARSE,
                };
            }

            onEvent?.(message, event as StreamEventType);
        },
        onclose: () => {
            if (!hasFailed) {
                onClose?.();
            }
        },
    });
};
